import React from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

import NumberFormat from '../../../enums/NumberFormat';
import LegendLayout from '../../../enums/LegendLayout';
import format from '../../../helpers/NumberFormatter';
import { roundNumberToPrecision } from '../../../helpers/Util';

import BubbleControls from './BubbleControls';
import BubblePreviewInfo from './BubblePreviewInfo';
import BubbleColorPalette from './BubbleColorPalette';
import BubbleGraphicsDetailed from './BubbleGraphicsDetailed';
import PagedBrowser from '../../PagedBrowser';
import OpportunityZonesLegend from '../layersLegend/OpportunityZonesLegend';
import SchoolRankLegend from '../layersLegend/SchoolRankLegend';
import BaseMap from '../../../enums/BaseMapTypes';

const CUSTOM_LIBRARY_LAYER_IDS = ['school_digger', 'opportunity_zones', BaseMap.STREET];

const RADII = {
    lowCircleRadius: 16,
    mediumCircleRadius: 28,
    highCircleRadius: 40,
    veryHighCircleRadius: 60,
};

const BubbleDetailed = props => {
    const {
        mapInstance,
        renderer,
        currentBubbleSizeLog10,
        onBubbleSizeChange,
        bubbleValueType,
        variableGuid,
        maxBubbleLabel,
        libraryLegendInfo,
    } = props;
    const { rules, bubbleSizeFactor } = renderer;

    const legendHighlighted = rules.some(rule => rule.highlighted);
    const veryHighCircleLabel = `${format({
        number: roundNumberToPrecision(
            ((RADII.veryHighCircleRadius ** 2) * Math.PI) / bubbleSizeFactor,
        ),
        numberFormat: NumberFormat.FORMAT_NUMBER_NO_DECIMAL,
    })}`;
    const highCircleLabel = format({
        number: roundNumberToPrecision(
            ((RADII.highCircleRadius ** 2) * Math.PI) / bubbleSizeFactor,
        ),
        numberFormat: NumberFormat.FORMAT_NUMBER_NO_DECIMAL,
    });
    const mediumCircleLabel = format({
        number: roundNumberToPrecision(
            ((RADII.mediumCircleRadius ** 2) * Math.PI) / bubbleSizeFactor,
        ),
        numberFormat: NumberFormat.FORMAT_NUMBER_NO_DECIMAL,
    });
    const lowCircleLabel = format({
        number: roundNumberToPrecision(
            ((RADII.lowCircleRadius ** 2) * Math.PI) / bubbleSizeFactor,
        ),
        numberFormat: NumberFormat.FORMAT_NUMBER_NO_DECIMAL,
    });

    const bubbleInfo = (
        <div
            key="first-page"
            className="bubble-legend flex-it column space-between"
        >
            <div className="flex-it space-around grow no-shrink">
                <BubbleGraphicsDetailed
                    veryHighCircleLabel={veryHighCircleLabel}
                    highCircleLabel={highCircleLabel}
                    mediumCircleLabel={mediumCircleLabel}
                    lowCircleLabel={lowCircleLabel}
                />
            </div>
            <BubbleControls
                currentBubbleSizeLog10={currentBubbleSizeLog10}
                onBubbleSizeChange={onBubbleSizeChange}
                showLabel
            />
            <BubblePreviewInfo maxBubbleLabel={maxBubbleLabel} />
            <BubbleColorPalette
                key="color-palette-page"
                bubbleValueType={bubbleValueType}
                layout={LegendLayout.DETAILED}
                variableGuid={variableGuid}
                renderer={renderer}
                mapInstanceId={mapInstance.id}
            />
        </div>
    );

    const pages = [bubbleInfo];

    libraryLegendInfo
        .filter(lg => CUSTOM_LIBRARY_LAYER_IDS.indexOf(lg.id) !== -1)
        .forEach(layerGroup => {
            switch (layerGroup.id) {
            case 'school_digger':
                pages.push(
                    <SchoolRankLegend
                        key="school-rank-legend"
                        mapInstance={mapInstance}
                    />,
                );
                break;
            case 'opportunity_zones':
                pages.push(
                    <OpportunityZonesLegend
                        key="opportunity-zones-legend"
                        mapInstance={mapInstance}
                    />,
                );
                break;
            }
        });

    const currentPageIndex = legendHighlighted ? 0 : undefined;

    return <PagedBrowser currentPage={currentPageIndex}>{pages}</PagedBrowser>;
};

BubbleDetailed.propTypes = {
    currentBubbleSizeLog10: PropTypes.number.isRequired,
    mapInstance: PropTypes.object.isRequired,
    renderer: PropTypes.object.isRequired,
    variableGuid: PropTypes.string.isRequired,
    bubbleValueType: PropTypes.string.isRequired,
    maxBubbleLabel: PropTypes.string.isRequired,
    onBubbleSizeChange: PropTypes.func.isRequired,
    libraryLegendInfo: PropTypes.array,
    intl: PropTypes.object.isRequired,
};

BubbleDetailed.defaultProps = {
    libraryLegendInfo: undefined,
};

export default injectIntl(BubbleDetailed);
